import { useState, useEffect, useMemo } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  ClipboardList, Plus, Search, Download, Trash2, Eye, Loader2, ArrowRightCircle, FileDown, Truck, X,
} from "lucide-react";
import { toast } from "sonner";

const formatINR = (n) => {
  const v = Number(n || 0);
  return `₹${v.toLocaleString("en-IN", { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`;
};
const todayStr = () => new Date().toISOString().split("T")[0];

const PO_STATUSES = ["Draft", "Sent", "Partially Fulfilled", "Fulfilled", "Cancelled"];

const statusBadgeClass = (s) => ({
  Draft: "bg-[#e0e7ff] text-[#3730a3]",
  Sent: "bg-[#fef3c7] text-[#92400e]",
  "Partially Fulfilled": "bg-[#fed7aa] text-[#9a3412]",
  Fulfilled: "bg-[#dcfce7] text-[#166534]",
  Cancelled: "bg-[#fee2e2] text-[#991b1b]",
}[s] || "bg-[#e2e8f0] text-[#475569]");

const emptyItem = () => ({ description: "", hsn_sac: "", quantity: 1, rate: 0, gst_rate: 18, unit: "NOS" });

export default function PurchaseOrdersPage() {
  const [pos, setPos] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    vendor_id: "", date: todayStr(), expected_delivery: "",
    terms_conditions: "", notes: "", items: [emptyItem()],
  });

  const [viewPo, setViewPo] = useState(null);
  const [converting, setConverting] = useState(null); // po_id being converted
  // Per-line GRN dialog
  const [receivePo, setReceivePo] = useState(null); // PO being received against
  const [receiveLines, setReceiveLines] = useState([]); // [{index, qty}] per line

  const refresh = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        api.get("/purchase-orders"),
        api.get("/vendors"),
      ]);
      setPos(r1.data || []);
      setVendors(r2.data || []);
    } catch (e) {
      toast.error("Failed to load purchase orders");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const filtered = useMemo(() => {
    return (pos || []).filter((p) => {
      if (statusFilter !== "all" && p.status !== statusFilter) return false;
      if (!search.trim()) return true;
      const s = search.trim().toLowerCase();
      return (p.po_number || "").toLowerCase().includes(s)
        || (p.vendor_name || "").toLowerCase().includes(s);
    });
  }, [pos, search, statusFilter]);

  // Inline-add a new line item
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, emptyItem()] }));
  const updateItem = (i, patch) => setForm((f) => ({
    ...f, items: f.items.map((it, idx) => idx === i ? { ...it, ...patch } : it),
  }));
  const removeItem = (i) => setForm((f) => ({
    ...f, items: f.items.filter((_, idx) => idx !== i),
  }));

  // Live total preview (client-side; backend recomputes)
  const totalPreview = useMemo(() => {
    let subtotal = 0, gst = 0;
    for (const it of form.items) {
      const q = Number(it.quantity || 0), r = Number(it.rate || 0), g = Number(it.gst_rate || 0);
      const ls = q * r;
      subtotal += ls;
      gst += ls * g / 100;
    }
    return { subtotal, gst, grand: subtotal + gst };
  }, [form.items]);

  const submitCreate = async () => {
    if (!form.vendor_id) { toast.error("Pick a vendor"); return; }
    if (!form.items.length || form.items.some((i) => !i.description || !i.rate)) {
      toast.error("Each item needs a description and rate");
      return;
    }
    setCreating(true);
    try {
      const r = await api.post("/purchase-orders", form);
      toast.success(`Purchase Order ${r.data.po_number} created`);
      setCreateOpen(false);
      setForm({ vendor_id: "", date: todayStr(), expected_delivery: "", terms_conditions: "", notes: "", items: [emptyItem()] });
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create PO");
    } finally {
      setCreating(false);
    }
  };

  const downloadPdf = async (po) => {
    try {
      const r = await api.get(`/purchase-orders/${po.po_id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${po.po_number.replace(/\//g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("PDF download failed");
    }
  };

  const openReceiveDialog = (po) => {
    // Pre-fill each open line with the remaining-to-receive qty
    const lines = (po.items || []).map((it, idx) => {
      const remaining = Math.max(0, Number(it.quantity || 0) - Number(it.qty_received || 0));
      return { index: idx, qty: remaining, max: remaining, description: it.description, ordered: it.quantity, alreadyReceived: it.qty_received || 0 };
    }).filter((l) => l.max > 0);
    setReceiveLines(lines);
    setReceivePo(po);
  };

  const submitReceive = async (mode /* "full" | "partial" */) => {
    if (!receivePo) return;
    setConverting(receivePo.po_id);
    try {
      let body = {};
      if (mode === "partial") {
        const lines = receiveLines
          .filter((l) => Number(l.qty) > 0)
          .map((l) => ({ index: l.index, qty: Number(l.qty) }));
        if (!lines.length) { toast.error("Enter at least one quantity"); setConverting(null); return; }
        // Validate no over-receive
        for (const l of receiveLines) {
          if (Number(l.qty) > l.max + 1e-6) {
            toast.error(`${l.description}: only ${l.max} remaining`);
            setConverting(null);
            return;
          }
        }
        body = { lines };
      }
      const r = await api.post(`/purchase-orders/${receivePo.po_id}/convert`, body);
      const po = r.data.purchase_order;
      if (r.data.already_converted) {
        toast.info(`Nothing left to receive on ${receivePo.po_number}.`);
      } else {
        toast.success(
          po.status === "Fulfilled"
            ? `${receivePo.po_number} fully received → Purchase ${r.data.purchase.purchase_id}`
            : `GRN #${r.data.grn.grn_seq} recorded — ${po.fulfilment_summary.total_received}/${po.fulfilment_summary.total_ordered} received`
        );
      }
      setReceivePo(null);
      setReceiveLines([]);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Receive failed");
    } finally {
      setConverting(null);
    }
  };

  const removePo = async (po) => {
    if (!window.confirm(`Delete PO ${po.po_number}? This cannot be undone.`)) return;
    try {
      await api.delete(`/purchase-orders/${po.po_id}`);
      toast.success("Purchase order deleted");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="purchase-orders-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-outfit text-2xl font-bold text-[#0f172a] flex items-center gap-2">
            <ClipboardList className="h-6 w-6 text-[#4338ca]" /> Purchase Orders
          </h1>
          <p className="text-sm text-[#64748b]">Draft, send, and convert POs into purchases.</p>
        </div>
        <Button data-testid="po-create-btn" className="bg-[#4338ca] hover:bg-[#3730a3] text-white" onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4 mr-1" /> New Purchase Order
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-white border-[#e2e8f0]">
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="h-4 w-4 text-[#94a3b8] absolute left-3 top-1/2 -translate-y-1/2" />
            <Input data-testid="po-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search PO number or vendor…" className="pl-9 text-sm" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="po-status-filter" className="w-[160px] text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {PO_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="text-xs text-[#64748b]" data-testid="po-count">{filtered.length} of {pos.length}</span>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="bg-white border-[#e2e8f0]">
        <CardContent className="p-0 overflow-x-auto">
          {loading ? (
            <div className="p-12 flex justify-center"><Loader2 className="animate-spin text-[#4338ca]" /></div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-sm text-[#64748b]" data-testid="po-empty">
              No purchase orders yet. Click <strong>New Purchase Order</strong> to draft one — or ask the Command Center: <em>&quot;Draft a PO for Acme for 50 licenses at 1200 each&quot;</em>.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[#f8fafc] text-xs uppercase text-[#64748b]">
                <tr>
                  <th className="text-left p-3">PO #</th>
                  <th className="text-left p-3">Date</th>
                  <th className="text-left p-3">Vendor</th>
                  <th className="text-right p-3">Amount</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-right p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.po_id} className="border-t border-[#e2e8f0] hover:bg-[#f8fafc]" data-testid={`po-row-${p.po_number}`}>
                    <td className="p-3 font-mono text-xs">{p.po_number}</td>
                    <td className="p-3 text-[#475569]">{p.date}</td>
                    <td className="p-3 font-medium text-[#0f172a]">{p.vendor_name}</td>
                    <td className="p-3 text-right font-mono">{formatINR(p.grand_total || p.total)}</td>
                    <td className="p-3">
                      <div className="flex flex-col gap-1">
                        <Badge className={statusBadgeClass(p.status)}>{p.status}</Badge>
                        {p.fulfilment_summary && p.fulfilment_summary.total_ordered > 0 && p.fulfilment_summary.any_received && !p.fulfilment_summary.fully_received && (
                          <div className="flex items-center gap-1.5" title={`${p.fulfilment_summary.total_received}/${p.fulfilment_summary.total_ordered} units received`}>
                            <div className="h-1.5 w-20 bg-[#e2e8f0] rounded-full overflow-hidden">
                              <div className="h-full bg-[#f59e0b]" style={{ width: `${Math.min(100, p.fulfilment_summary.percent)}%` }} />
                            </div>
                            <span className="text-[10px] font-mono text-[#92400e]">{p.fulfilment_summary.total_received}/{p.fulfilment_summary.total_ordered}</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="p-3 text-right">
                      <div className="inline-flex gap-1">
                        <Button size="sm" variant="ghost" title="View" onClick={() => setViewPo(p)} data-testid={`po-view-${p.po_number}`}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" title="Download PDF" onClick={() => downloadPdf(p)} data-testid={`po-pdf-${p.po_number}`}>
                          <FileDown className="h-4 w-4" />
                        </Button>
                        {p.status !== "Fulfilled" && p.status !== "Cancelled" && (
                          <Button
                            size="sm" variant="ghost" title="Receive / Convert"
                            onClick={() => openReceiveDialog(p)}
                            disabled={converting === p.po_id}
                            data-testid={`po-convert-${p.po_number}`}
                          >
                            {converting === p.po_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRightCircle className="h-4 w-4 text-[#10b981]" />}
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" title="Delete" onClick={() => removePo(p)} data-testid={`po-delete-${p.po_number}`}>
                          <Trash2 className="h-4 w-4 text-[#ef4444]" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="po-create-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit">New Purchase Order</DialogTitle>
                <DialogDescription>Draft a PO. It will be added with status &quot;Draft&quot; — you can mark it Sent later.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-[#64748b]">Vendor</Label>
                <Select value={form.vendor_id} onValueChange={(v) => setForm({ ...form, vendor_id: v })}>
                  <SelectTrigger data-testid="po-vendor-select"><SelectValue placeholder="Pick a vendor" /></SelectTrigger>
                  <SelectContent>
                    {vendors.length === 0 && <div className="px-3 py-2 text-xs text-[#94a3b8]">No vendors yet. Add one from the Vendors page.</div>}
                    {vendors.map((v) => <SelectItem key={v.vendor_id} value={v.vendor_id}>{v.name}{v.state ? ` · ${v.state}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-[#64748b]">PO Date</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="po-date" />
              </div>
              <div>
                <Label className="text-xs text-[#64748b]">Expected Delivery</Label>
                <Input type="date" value={form.expected_delivery} onChange={(e) => setForm({ ...form, expected_delivery: e.target.value })} data-testid="po-expected-delivery" />
              </div>
            </div>

            {/* Items */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs text-[#64748b]">Line Items</Label>
                <Button type="button" size="sm" variant="outline" onClick={addItem} data-testid="po-add-item">
                  <Plus className="h-3 w-3 mr-1" /> Add item
                </Button>
              </div>
              <div className="space-y-2">
                {form.items.map((it, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`po-item-row-${i}`}>
                    <div className="col-span-4">
                      <Input value={it.description} onChange={(e) => updateItem(i, { description: e.target.value })} placeholder="Description" data-testid={`po-item-desc-${i}`} />
                    </div>
                    <div className="col-span-2">
                      <Input value={it.hsn_sac} onChange={(e) => updateItem(i, { hsn_sac: e.target.value })} placeholder="HSN/SAC" data-testid={`po-item-hsn-${i}`} />
                    </div>
                    <div className="col-span-1">
                      <Input type="number" value={it.quantity} onChange={(e) => updateItem(i, { quantity: Number(e.target.value) })} placeholder="Qty" data-testid={`po-item-qty-${i}`} />
                    </div>
                    <div className="col-span-2">
                      <Input type="number" value={it.rate} onChange={(e) => updateItem(i, { rate: Number(e.target.value) })} placeholder="Rate" data-testid={`po-item-rate-${i}`} />
                    </div>
                    <div className="col-span-2">
                      <Input type="number" value={it.gst_rate} onChange={(e) => updateItem(i, { gst_rate: Number(e.target.value) })} placeholder="GST %" data-testid={`po-item-gst-${i}`} />
                    </div>
                    <div className="col-span-1">
                      {form.items.length > 1 && (
                        <Button type="button" size="icon" variant="ghost" onClick={() => removeItem(i)} data-testid={`po-item-remove-${i}`}>
                          <X className="h-4 w-4 text-[#ef4444]" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[#64748b]">Terms & Conditions</Label>
                <Textarea rows={3} value={form.terms_conditions} onChange={(e) => setForm({ ...form, terms_conditions: e.target.value })} placeholder="Payment terms, delivery, warranties…" data-testid="po-terms" />
              </div>
              <div>
                <Label className="text-xs text-[#64748b]">Notes</Label>
                <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Internal notes" data-testid="po-notes" />
              </div>
            </div>

            <div className="bg-[#f8fafc] rounded-lg p-3 text-sm space-y-1" data-testid="po-totals-preview">
              <div className="flex justify-between"><span className="text-[#64748b]">Subtotal</span><span className="font-mono">{formatINR(totalPreview.subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-[#64748b]">GST (estimated)</span><span className="font-mono">{formatINR(totalPreview.gst)}</span></div>
              <div className="flex justify-between font-bold text-[#0f172a]"><span>Grand Total</span><span className="font-mono">{formatINR(totalPreview.grand)}</span></div>
              <p className="text-[10px] text-[#94a3b8] pt-1">CGST/SGST vs IGST split is computed server-side based on vendor state.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={creating}>Cancel</Button>
            <Button onClick={submitCreate} disabled={creating} data-testid="po-create-submit" className="bg-[#4338ca] hover:bg-[#3730a3] text-white">
              {creating ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Creating…</> : "Create Purchase Order"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View dialog */}
      <Dialog open={!!viewPo} onOpenChange={(o) => { if (!o) setViewPo(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="po-view-dialog">
          {viewPo && (
            <>
              <DialogHeader>
                <DialogTitle className="font-outfit flex items-center gap-2">
                  <ClipboardList className="h-5 w-5 text-[#4338ca]" /> {viewPo.po_number}
                  <Badge className={statusBadgeClass(viewPo.status)}>{viewPo.status}</Badge>
                </DialogTitle>
                <DialogDescription className="flex items-center gap-2"><Truck className="h-3 w-3" /> {viewPo.vendor_name} · {viewPo.date}</DialogDescription>
              </DialogHeader>
              <div className="mt-4 space-y-3">
                <table className="w-full text-sm border border-[#e2e8f0] rounded">
                  <thead className="bg-[#f8fafc] text-xs uppercase text-[#64748b]">
                    <tr><th className="p-2 text-left">Description</th><th>HSN</th><th>Qty</th><th>Rate</th><th>GST%</th><th className="text-right">Total</th></tr>
                  </thead>
                  <tbody>
                    {(viewPo.items || []).map((it, i) => (
                      <tr key={i} className="border-t border-[#e2e8f0]">
                        <td className="p-2">{it.description}</td>
                        <td className="text-center">{it.hsn_sac || "-"}</td>
                        <td className="text-center">{it.quantity}</td>
                        <td className="text-right font-mono">{formatINR(it.rate)}</td>
                        <td className="text-center">{it.gst_rate}%</td>
                        <td className="text-right font-mono">{formatINR(it.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="text-[#64748b]">
                    {viewPo.terms_conditions && <><strong>Terms:</strong><br /><span className="whitespace-pre-line">{viewPo.terms_conditions}</span></>}
                  </div>
                  <div className="space-y-1 text-right">
                    <div className="flex justify-between"><span className="text-[#64748b]">Subtotal</span><span className="font-mono">{formatINR(viewPo.subtotal)}</span></div>
                    {viewPo.interstate
                      ? <div className="flex justify-between"><span className="text-[#64748b]">IGST</span><span className="font-mono">{formatINR(viewPo.igst_amount)}</span></div>
                      : (<>
                        <div className="flex justify-between"><span className="text-[#64748b]">CGST</span><span className="font-mono">{formatINR(viewPo.cgst_amount)}</span></div>
                        <div className="flex justify-between"><span className="text-[#64748b]">SGST</span><span className="font-mono">{formatINR(viewPo.sgst_amount)}</span></div>
                      </>)}
                    <div className="flex justify-between font-bold text-[#0f172a] pt-1 border-t border-[#e2e8f0]"><span>Grand Total</span><span className="font-mono">{formatINR(viewPo.grand_total)}</span></div>
                  </div>
                </div>
                {/* GRN history */}
                {(viewPo.grn_history || []).length > 0 && (
                  <div className="border border-[#e2e8f0] rounded-lg p-3" data-testid="po-grn-history">
                    <p className="text-xs font-semibold text-[#475569] mb-2 uppercase tracking-wide">Goods Receipt History</p>
                    <div className="space-y-1.5">
                      {viewPo.grn_history.map((g) => (
                        <div key={g.grn_seq} className="flex items-center justify-between text-xs">
                          <span className="text-[#0f172a]">
                            <strong>GRN #{g.grn_seq}</strong> · {g.date} ·{" "}
                            {g.lines.map((ln) => `${ln.qty} of "${viewPo.items[ln.index]?.description || 'item'}"`).join(", ")}
                          </span>
                          <span className="font-mono text-[#10b981]">{formatINR(g.received_amount)}</span>
                        </div>
                      ))}
                    </div>
                    {viewPo.fulfilment_summary && (
                      <div className="mt-2 pt-2 border-t border-[#e2e8f0] flex items-center justify-between text-xs">
                        <span className="text-[#475569]">Overall: <strong>{viewPo.fulfilment_summary.total_received}/{viewPo.fulfilment_summary.total_ordered}</strong> units</span>
                        <span className="font-mono text-[#92400e]">{viewPo.fulfilment_summary.percent}%</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => downloadPdf(viewPo)} data-testid="po-view-pdf">
                  <Download className="h-4 w-4 mr-1" /> Download PDF
                </Button>
                {viewPo.status !== "Fulfilled" && viewPo.status !== "Cancelled" && (
                  <Button className="bg-[#10b981] hover:bg-[#059669] text-white" onClick={() => { openReceiveDialog(viewPo); setViewPo(null); }} data-testid="po-view-convert">
                    <ArrowRightCircle className="h-4 w-4 mr-1" /> Receive / Convert
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Receive (GRN) dialog */}
      <Dialog open={!!receivePo} onOpenChange={(o) => { if (!o) { setReceivePo(null); setReceiveLines([]); } }}>
        <DialogContent className="max-w-xl" data-testid="po-receive-dialog">
          {receivePo && (
            <>
              <DialogHeader>
                <DialogTitle className="font-outfit flex items-center gap-2">
                  <ArrowRightCircle className="h-5 w-5 text-[#10b981]" /> Receive against {receivePo.po_number}
                </DialogTitle>
                <DialogDescription>
                  Record a delivery (GRN). You can receive everything, or enter partial quantities per line.
                </DialogDescription>
              </DialogHeader>
              {receiveLines.length === 0 ? (
                <div className="py-4 text-sm text-[#64748b]">Nothing left to receive on this PO.</div>
              ) : (
                <div className="space-y-2 mt-2">
                  {receiveLines.map((l, i) => (
                    <div key={l.index} className="grid grid-cols-12 gap-2 items-center" data-testid={`po-receive-line-${l.index}`}>
                      <div className="col-span-6">
                        <div className="text-sm font-medium text-[#0f172a]">{l.description}</div>
                        <div className="text-[10px] text-[#94a3b8]">Ordered {l.ordered} · Already received {l.alreadyReceived} · Remaining {l.max}</div>
                      </div>
                      <div className="col-span-4">
                        <Input
                          type="number"
                          value={l.qty}
                          min={0}
                          max={l.max}
                          step="any"
                          onChange={(e) => {
                            const v = Number(e.target.value);
                            setReceiveLines((prev) => prev.map((x, j) => j === i ? { ...x, qty: v } : x));
                          }}
                          data-testid={`po-receive-qty-${l.index}`}
                        />
                      </div>
                      <div className="col-span-2 text-right text-xs">
                        <button
                          type="button"
                          className="text-[#4338ca] hover:underline"
                          onClick={() => setReceiveLines((prev) => prev.map((x, j) => j === i ? { ...x, qty: x.max } : x))}
                          data-testid={`po-receive-fill-${l.index}`}
                        >
                          Fill max
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <DialogFooter className="gap-2 mt-3">
                <Button variant="outline" onClick={() => { setReceivePo(null); setReceiveLines([]); }} disabled={converting === receivePo.po_id}>
                  Cancel
                </Button>
                <Button
                  variant="outline"
                  className="border-[#10b981] text-[#10b981] hover:bg-[#ecfdf5]"
                  onClick={() => submitReceive("full")}
                  disabled={converting === receivePo.po_id || receiveLines.length === 0}
                  data-testid="po-receive-all-btn"
                >
                  Receive All Remaining
                </Button>
                <Button
                  className="bg-[#4338ca] hover:bg-[#3730a3] text-white"
                  onClick={() => submitReceive("partial")}
                  disabled={converting === receivePo.po_id || receiveLines.length === 0}
                  data-testid="po-receive-partial-btn"
                >
                  {converting === receivePo.po_id ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Recording…</> : "Record GRN"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
